import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BookOpen, Save, PlugZap, Loader2, CheckCircle2, XCircle, AlertCircle,
  RefreshCcw, Package, Users, Warehouse as WarehouseIcon, Boxes, Info,
  Search, ExternalLink, Trash2, Link2, PlayCircle, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR");
  } catch { return String(iso); }
}
function fmtMoney(n, currency = "TL") {
  const v = Number(n || 0);
  return `${v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();
}

export default function BizimHesapTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(null); // null | 'all' | 'products' | ...
  const [form, setForm] = useState({ token: "", api_key: "", base_url: "", firm_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bizim-hesap/settings");
      setCfg(data);
      setForm((f) => ({
        ...f,
        // Never load token back — it stays server-side. Show placeholder if set.
        token: "",
        api_key: "",
        base_url: data.base_url || "",
        firm_id: data.firm_id || "",
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        token: form.token || "",
        api_key: form.api_key || "",
        base_url: form.base_url || "",
        firm_id: form.firm_id || "",
      };
      const { data } = await api.post("/bizim-hesap/settings", payload);
      setCfg(data);
      setForm((f) => ({ ...f, token: "", api_key: "" }));
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/bizim-hesap/test");
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, configured: !!cfg?.configured, detail: formatApiError(e) });
    } finally {
      setTesting(false);
      load();
    }
  };

  const doSync = async (entity) => {
    setSyncing(entity);
    try {
      const { data } = await api.post(`/bizim-hesap/sync/${entity}`);
      if (entity === "all") {
        toast.success(
          `Senkron OK · Ürün ${data.products?.upserted ?? 0} · Cari ${data.customers?.upserted ?? 0} · Depo ${data.warehouses?.upserted ?? 0} · Stok ${data.inventory?.upserted ?? 0}`
        );
      } else {
        toast.success(`Senkron OK · ${data.upserted} kayıt`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(null);
      load();
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Kayıtlı Bizim Hesap token'ı silinsin mi? Aynalanmış veriler kalır.")) return;
    try {
      const { data } = await api.post("/bizim-hesap/disconnect");
      setCfg(data);
      toast.success("Bağlantı kesildi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Ayarlar yükleniyor...
      </div>
    );
  }

  return (
    <div className="space-y-6 bh-tab" data-testid="bizim-hesap-tab">
      {/* Header */}
      <div>
        <h3 className="text-slate-100 text-lg font-semibold flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-emerald-400" /> Bizim Hesap Entegrasyonu
        </h3>
        <p className="text-slate-400 text-xs mt-1">
          <strong>Salt okunur (Faz 1):</strong> Bizim Hesap'tan ürün, cari, depo ve stok verilerini çeker.
          ERP verilerini <em>otomatik</em> güncellemez — mirror'a düşen kayıtları buradan görebilirsiniz.
        </p>
      </div>

      {/* Credentials */}
      <Card className="p-5 space-y-4 bg-slate-900/40 border-slate-800 rounded-sm" data-testid="bh-credentials-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-slate-100 font-semibold text-sm">Bağlantı Bilgileri</div>
            <div className="text-slate-400 text-[11px] mt-0.5">
              Token'ı Bizim Hesap panelinden (E-Ticaret &gt; Ayarlar) veya destek@bizimhesap.com'dan alabilirsiniz.
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <StatusBadge configured={cfg?.configured} status={cfg?.last_sync_status} />
            {cfg?.last_sync_at && (
              <span className="text-slate-500" data-testid="bh-last-sync">Son senkron: {fmtDate(cfg.last_sync_at)}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Token (zorunlu)">
            <Input
              type="password"
              data-testid="bh-input-token"
              value={form.token}
              onChange={(e) => setForm({ ...form, token: e.target.value })}
              placeholder={cfg?.token_set ? "•••••• (kayıtlı — boş bırakırsanız değişmez)" : "Bizim Hesap panelinden alınan token"}
              className="bg-slate-950 border-slate-800 rounded-sm"
              autoComplete="new-password"
            />
          </Field>
          <Field label="API Key (opsiyonel — genelde varsayılan yeterli)">
            <Input
              type="password"
              data-testid="bh-input-key"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder={cfg?.api_key_set ? "•••••• (varsayılan public key kayıtlı)" : "BZMHB2B..."}
              className="bg-slate-950 border-slate-800 rounded-sm"
              autoComplete="new-password"
            />
          </Field>
          <Field label="Base URL">
            <Input
              data-testid="bh-input-baseurl"
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder="https://bizimhesap.com/api/b2b"
              className="bg-slate-950 border-slate-800 rounded-sm font-mono text-xs"
            />
          </Field>
          <Field label="FirmID (opsiyonel — fatura yazımı Faz 2 için)">
            <Input
              data-testid="bh-input-firmid"
              value={form.firm_id}
              onChange={(e) => setForm({ ...form, firm_id: e.target.value })}
              placeholder="Boş bırakabilirsiniz"
              className="bg-slate-950 border-slate-800 rounded-sm font-mono text-xs"
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="bh-save-btn"
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Kaydet
          </Button>
          <Button
            onClick={runTest}
            disabled={testing || !cfg?.configured}
            variant="outline"
            data-testid="bh-test-btn"
            className="border-slate-700 text-slate-200 rounded-sm"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
            Bağlantıyı Test Et
          </Button>
          {cfg?.token_set && (
            <Button
              onClick={disconnect}
              variant="outline"
              data-testid="bh-disconnect-btn"
              className="border-rose-800 text-rose-300 hover:bg-rose-950/40 rounded-sm"
            >
              <XCircle className="w-4 h-4 mr-2" /> Bağlantıyı Kes
            </Button>
          )}
        </div>

        {testResult && (
          <div
            className={`text-xs px-3 py-2 rounded-sm border ${
              testResult.ok
                ? "bg-emerald-950/40 border-emerald-800 text-emerald-300"
                : "bg-rose-950/40 border-rose-800 text-rose-300"
            }`}
            data-testid="bh-test-result"
          >
            {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : <XCircle className="w-3.5 h-3.5 inline mr-1" />}
            {testResult.detail || (testResult.ok ? "OK" : "Başarısız")}
          </div>
        )}

        {cfg?.last_sync_status === "error" && cfg?.last_sync_error && (
          <div className="text-xs px-3 py-2 rounded-sm border bg-amber-950/40 border-amber-800 text-amber-300" data-testid="bh-last-error">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
            Son senkron hatası: {cfg.last_sync_error}
          </div>
        )}
      </Card>

      {/* Sync actions */}
      <Card className="p-5 space-y-3 bg-slate-900/40 border-slate-800 rounded-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-slate-100 font-semibold text-sm">Senkronizasyon</div>
            <div className="text-slate-400 text-[11px]">Bizim Hesap'tan veri çek → ERP mirror'a yaz. Yerel ERP verileri değişmez.</div>
          </div>
          <Button
            onClick={() => doSync("all")}
            disabled={!cfg?.configured || !!syncing}
            data-testid="bh-sync-all-btn"
            className="bg-blue-600 hover:bg-blue-500 rounded-sm"
          >
            {syncing === "all" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Tümünü Senkronize Et
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SyncTile icon={<Package className="w-4 h-4" />} label="Ürünler" count={cfg?.last_sync_counts?.products}
            onClick={() => doSync("products")} busy={syncing === "products"} disabled={!cfg?.configured || !!syncing}
            testId="bh-sync-products-btn" />
          <SyncTile icon={<Users className="w-4 h-4" />} label="Cariler" count={cfg?.last_sync_counts?.customers}
            onClick={() => doSync("customers")} busy={syncing === "customers"} disabled={!cfg?.configured || !!syncing}
            testId="bh-sync-customers-btn" />
          <SyncTile icon={<WarehouseIcon className="w-4 h-4" />} label="Depolar" count={cfg?.last_sync_counts?.warehouses}
            onClick={() => doSync("warehouses")} busy={syncing === "warehouses"} disabled={!cfg?.configured || !!syncing}
            testId="bh-sync-warehouses-btn" />
          <SyncTile icon={<Boxes className="w-4 h-4" />} label="Stok" count={cfg?.last_sync_counts?.inventory}
            onClick={() => doSync("inventory")} busy={syncing === "inventory"} disabled={!cfg?.configured || !!syncing}
            testId="bh-sync-inventory-btn" />
        </div>
        <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-950/50 border border-slate-800 rounded-sm px-3 py-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong className="text-slate-400">Not:</strong> Bizim Hesap resmi B2B API'sinde sipariş/fatura <em>okuma</em> uç noktası yayınlanmamış; sadece <em>yazma</em> (POST addinvoice) desteklenir.
            Bu nedenle Faz 1'de sipariş senkronu yer almıyor.{" "}
            <a href="https://apidocs.bizimhesap.com/" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline inline-flex items-center gap-0.5">
              API dokümanı <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </Card>

      {/* Bakiye Güncelle — Senkronizasyon bölümünün hemen altında */}
      <OneShotBalanceRecompute />

      {/* Mirrored data browser */}
      <MirrorBrowser configured={!!cfg?.configured} />

      {/* Tek seferlik: Tüm carileri BH'den çek + ERP'ye aktar (bakiye dahil).
          İş bittikten sonra bu blok güvenle kaldırılabilir. */}
      <OneShotCustomerFullSync />
    </div>
  );
}

function OneShotBalanceRecompute() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const run = async () => {
    if (!window.confirm("Mevcut staging verisinden ERP müşteri bakiyeleri güncellenecek. Bizim Hesap API'sine istek atılmayacak. Devam?")) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/bizim-hesap/recompute-customer-balances");
      setResult(data);
      toast.success(`Bakiye güncellendi · matched=${data.matched} · modified=${data.modified}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bakiye güncelleme hatası");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 rounded-sm bg-blue-950/20 border-blue-800/60" data-testid="oneshot-balance-recompute-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-blue-200">Bakiye Güncelle</div>
          <div className="text-xs text-blue-100/70 mt-1">
            Mevcut Bizim Hesap staging verilerinden (yeni API çağrısı yapmadan)
            linked ERP müşterilerin bh_balance/debit/credit alanlarını yeniden hesaplar.
            Rate-limit güvenli. Yeni müşteri yaratmaz.
          </div>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          data-testid="oneshot-balance-recompute-btn"
          className="bg-blue-700 hover:bg-blue-600 rounded-sm text-white text-xs uppercase tracking-wider"
        >
          {busy ? "Çalışıyor…" : "Bakiye Güncelle"}
        </Button>
      </div>
      {result && (
        <div className="mt-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-2 text-slate-200" data-testid="oneshot-balance-recompute-result">
          <div>Scanned: <b>{result.scanned}</b></div>
          <div>Matched: <b className="text-blue-300">{result.matched}</b></div>
          <div>Modified: <b className="text-emerald-300">{result.modified}</b></div>
          <div>Unmatched: <b className={result.unmatched ? "text-amber-300" : ""}>{result.unmatched}</b></div>
        </div>
      )}
    </Card>
  );
}

function OneShotCustomerFullSync() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const runFull = async () => {
    if (!window.confirm("Tüm Bizim Hesap carileri çekilip ERP'ye aktarılacak. Devam edilsin mi?")) return;
    setBusy(true); setResult(null);
    try {
      const r1 = await api.post("/bizim-hesap/sync/customers");
      const r2 = await api.post("/bizim-hesap/import/customers/all?dry_run=false");
      const r3 = await api.post("/bizim-hesap/recompute-customer-balances");
      const c = r2.data?.counts || {};
      setResult({
        fetched: r1.data?.fetched ?? 0,
        upserted_staging: r1.data?.upserted ?? 0,
        balance_pushed: r1.data?.extra?.balance_pushed_to_erp ?? 0,
        erp_created: c.created ?? 0,
        erp_matched: c.matched ?? 0,
        erp_errors: c.errors ?? 0,
        recomputed_scanned: r3.data?.scanned ?? 0,
        recomputed_matched: r3.data?.matched ?? 0,
        recomputed_modified: r3.data?.modified ?? 0,
        recomputed_unmatched: r3.data?.unmatched ?? 0,
      });
      toast.success(`Tamam · ${c.created ?? 0} yeni · ${c.matched ?? 0} eşleşen · ${r3.data?.modified ?? 0} bakiye yeniden hesaplandı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cari tam sync hatası");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 rounded-sm bg-amber-950/20 border-amber-800/60" data-testid="oneshot-customer-sync-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-200">Tek Seferlik · Cari Tam Senkron</div>
          <div className="text-xs text-amber-100/70 mt-1">
            Tüm Bizim Hesap carilerini çeker, staging'i günceller ve linked ERP müşterilere bh_balance/debit/credit yazar. Eksik carileri de ekler. Mevcut kayıtlar silinmez.
          </div>
        </div>
        <Button
          onClick={runFull}
          disabled={busy}
          data-testid="oneshot-customer-sync-btn"
          className="bg-amber-700 hover:bg-amber-600 rounded-sm text-white text-xs uppercase tracking-wider"
        >
          {busy ? "Çalışıyor…" : "Şimdi Çalıştır"}
        </Button>
      </div>
      {result && (
        <div className="mt-3 text-xs grid grid-cols-2 md:grid-cols-3 gap-2 text-slate-200" data-testid="oneshot-customer-sync-result">
          <div>Staging fetched: <b>{result.fetched}</b></div>
          <div>Staging upserted: <b>{result.upserted_staging}</b></div>
          <div>Bakiye güncellenen (linked): <b>{result.balance_pushed}</b></div>
          <div>ERP'ye yeni eklenen: <b className="text-emerald-300">{result.erp_created}</b></div>
          <div>ERP'de eşleşen: <b>{result.erp_matched}</b></div>
          <div>Hata: <b className={result.erp_errors ? "text-rose-300" : ""}>{result.erp_errors}</b></div>
          <div>Recompute scanned: <b>{result.recomputed_scanned}</b></div>
          <div>Recompute matched: <b className="text-blue-300">{result.recomputed_matched}</b></div>
          <div>Recompute modified: <b className="text-emerald-300">{result.recomputed_modified}</b></div>
          <div>Recompute unmatched: <b className={result.recomputed_unmatched ? "text-amber-300" : ""}>{result.recomputed_unmatched}</b></div>
        </div>
      )}
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono">{label}</Label>
      {children}
    </div>
  );
}

function StatusBadge({ configured, status }) {
  if (!configured) return <span className="px-2 py-0.5 rounded-sm bg-slate-800 text-slate-400 text-[10px] uppercase tracking-widest font-mono">Yapılandırılmamış</span>;
  if (status === "ok") return <span className="px-2 py-0.5 rounded-sm bg-emerald-900/40 text-emerald-300 text-[10px] uppercase tracking-widest font-mono border border-emerald-800">Bağlı</span>;
  if (status === "error") return <span className="px-2 py-0.5 rounded-sm bg-rose-900/40 text-rose-300 text-[10px] uppercase tracking-widest font-mono border border-rose-800">Hata</span>;
  return <span className="px-2 py-0.5 rounded-sm bg-slate-800 text-slate-300 text-[10px] uppercase tracking-widest font-mono">Hazır</span>;
}

function SyncTile({ icon, label, count, onClick, busy, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="text-left px-3 py-2.5 rounded-sm border border-slate-800 bg-slate-950 hover:border-emerald-700 hover:bg-slate-900 disabled:opacity-50 disabled:hover:border-slate-800 transition-colors"
    >
      <div className="flex items-center gap-2 text-slate-300 text-xs">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> : icon}
        <span>{label}</span>
      </div>
      <div className="text-slate-500 text-[10px] mt-1 font-mono">
        {typeof count === "number" ? `${count} kayıt` : "senkronize edilmedi"}
      </div>
    </button>
  );
}

// ---------------------------- Mirror browser ---------------------------------

function MirrorBrowser({ configured }) {
  const [tab, setTab] = useState("products");
  return (
    <Card className="p-5 bg-slate-900/40 border-slate-800 rounded-sm" data-testid="bh-mirror-card">
      <div className="text-slate-100 font-semibold text-sm mb-3">Aynalanan Veriler</div>
      {!configured ? (
        <div className="text-xs text-slate-500 py-6 text-center">Önce token'ı kaydedip senkronize edin.</div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-950 border border-slate-800 rounded-sm p-0.5 flex-wrap h-auto">
            <TabsTrigger value="products" data-testid="bh-tab-products" className="rounded-sm">
              <Package className="w-3.5 h-3.5 mr-1" /> Ürünler
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="bh-tab-customers" className="rounded-sm">
              <Users className="w-3.5 h-3.5 mr-1" /> Cariler
            </TabsTrigger>
            <TabsTrigger value="warehouses" data-testid="bh-tab-warehouses" className="rounded-sm">
              <WarehouseIcon className="w-3.5 h-3.5 mr-1" /> Depolar
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="bh-tab-inventory" className="rounded-sm">
              <Boxes className="w-3.5 h-3.5 mr-1" /> Stok
            </TabsTrigger>
            <TabsTrigger value="mapping" data-testid="bh-tab-mapping" className="rounded-sm">
              <WarehouseIcon className="w-3.5 h-3.5 mr-1" /> Depo Eşleştirme
            </TabsTrigger>
            <TabsTrigger value="risk" data-testid="bh-tab-risk" className="rounded-sm">
              <Package className="w-3.5 h-3.5 mr-1" /> Üretim Riski
            </TabsTrigger>
            <TabsTrigger value="variant-mapping" data-testid="bh-tab-variant-mapping" className="rounded-sm">
              <Boxes className="w-3.5 h-3.5 mr-1" /> Varyant Eşleştirme
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products" className="mt-4"><ProductsList /></TabsContent>
          <TabsContent value="customers" className="mt-4"><CustomersList /></TabsContent>
          <TabsContent value="warehouses" className="mt-4"><WarehousesList /></TabsContent>
          <TabsContent value="inventory" className="mt-4"><InventoryList /></TabsContent>
          <TabsContent value="mapping" className="mt-4"><WarehouseMappingPanel /></TabsContent>
          <TabsContent value="risk" className="mt-4"><ProductionRiskPanel /></TabsContent>
          <TabsContent value="variant-mapping" className="mt-4"><VariantMappingPanel /></TabsContent>
        </Tabs>
      )}
    </Card>
  );
}

function SearchBar({ q, setQ, placeholder = "Ara..." }) {
  return (
    <div className="relative">
      <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="bg-slate-950 border-slate-800 rounded-sm pl-8 h-8 text-xs"
        data-testid="bh-search-input"
      />
    </div>
  );
}

function useMirror(endpoint, params = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const key = JSON.stringify(params);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(endpoint, { params: { ...params, search: q } });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) { toast.error(formatApiError(e)); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, key, q, tick]);
  return { rows, loading, q, setQ, reload: () => setTick((n) => n + 1) };
}

function EmptyRow({ colSpan, loading }) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center text-slate-500 text-xs py-6">
        {loading ? "Yükleniyor..." : "Kayıt yok. Yukarıdaki 'Senkronize Et' butonunu çalıştırın."}
      </td>
    </tr>
  );
}

function ImportPreviewModal({ open, onClose, kind, items, onConfirm, busy, decisions, setDecisions }) {
  if (!open) return null;
  const c = items.counts || {};
  const rows = items.items || [];
  // FAZ B M2 — candidate satırlarında kullanıcı seçimi ("link" | "new" | "skip")
  // Varsayılan: "new" (auto-link YAPILMAZ).
  const decide = (bh_id, action) => setDecisions && setDecisions((d) => ({ ...d, [bh_id]: action }));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="import-preview-modal">
      <div className="bg-slate-950 border border-slate-800 rounded-sm max-w-3xl w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <div className="text-slate-100 font-semibold text-sm">
            {kind === "products" ? "Ürün" : "Cari"} Aktarım Önizlemesi
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex gap-3 flex-wrap font-mono">
            <span>Toplam: <span className="text-slate-200">{rows.length}</span></span>
            <span className="text-emerald-400">Yeni: {c.new || 0}</span>
            <span className="text-blue-400">Eşleşme (source_id): {c.matched || 0}</span>
            <span className="text-fuchsia-400" data-testid="preview-candidate-count">Olası (barcode): {c.candidate || 0}</span>
            <span className="text-amber-400">Zaten aktarılmış: {c.already_linked || 0}</span>
            <span className="text-slate-500">Atlanan: {c.skipped || 0}</span>
            <span className="text-rose-400">Eksik/Hata: {(c.missing || 0) + (c.error || 0)}</span>
          </div>
          {(c.candidate || 0) > 0 && (
            <div className="mt-2 p-2 border border-fuchsia-900/60 bg-fuchsia-950/20 rounded-sm text-[11px] text-fuchsia-300" data-testid="preview-candidate-warning">
              <strong>Olası eşleşme(ler)</strong>: Barcode ile mevcut bir ERP ürününe benziyor.
              Otomatik BAĞLANMAZ — her satır için "Mevcutla Eşleştir / Yeni Oluştur / Atla" seçin.
            </div>
          )}
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => {
                const dec = decisions?.[r.bh_id] || "new";
                return (
                  <tr key={r.bh_id} className="text-slate-300" data-testid={`preview-row-${r.status}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={
                        r.status === "new" ? "text-emerald-400" :
                        r.status === "matched" ? "text-blue-400" :
                        r.status === "candidate" ? "text-fuchsia-400" :
                        r.status === "already_linked" ? "text-amber-400" :
                        r.status === "skipped" ? "text-slate-500" :
                        "text-rose-400"
                      }>
                        {r.status === "new" ? "YENİ" :
                         r.status === "matched" ? "EŞLEŞME" :
                         r.status === "candidate" ? "OLASI" :
                         r.status === "already_linked" ? "ZATEN VAR" :
                         r.status === "skipped" ? "ATLANDI" :
                         r.status === "missing" ? "EKSİK" : "HATA"}
                      </span>
                    </td>
                    <td className="px-3 py-2">{r.title || r.bh_id}</td>
                    <td className="px-3 py-2 text-slate-500 text-[10px]">
                      {r.erp_name && <span>→ {r.erp_name}</span>}
                      {r.candidate_erp_name && (
                        <span>
                          aday → <span className="text-slate-300">{r.candidate_erp_name}</span>
                          {r.candidate_match_reason && ` (${r.candidate_match_reason})`}
                        </span>
                      )}
                      {r.reason && r.reason}
                    </td>
                    {r.status === "candidate" && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex gap-1 text-[10px]">
                          <button
                            data-testid={`candidate-link-${r.bh_id}`}
                            onClick={() => decide(r.bh_id, "link")}
                            className={`px-2 py-0.5 rounded-sm border ${dec === "link" ? "bg-blue-600 border-blue-500 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-900"}`}
                          >Eşleştir</button>
                          <button
                            data-testid={`candidate-new-${r.bh_id}`}
                            onClick={() => decide(r.bh_id, "new")}
                            className={`px-2 py-0.5 rounded-sm border ${dec === "new" ? "bg-emerald-600 border-emerald-500 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-900"}`}
                          >Yeni Oluştur</button>
                          <button
                            data-testid={`candidate-skip-${r.bh_id}`}
                            onClick={() => decide(r.bh_id, "skip")}
                            className={`px-2 py-0.5 rounded-sm border ${dec === "skip" ? "bg-slate-600 border-slate-500 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-900"}`}
                          >Atla</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-900">İptal</button>
          <button
            data-testid="import-confirm-btn"
            onClick={onConfirm}
            disabled={busy || ((c.new || 0) + (c.matched || 0) + (c.candidate || 0)) === 0}
            className="px-4 py-1.5 text-xs rounded-sm bg-blue-500 hover:bg-blue-600 text-slate-950 font-semibold disabled:opacity-40"
          >
            {busy ? "Aktarılıyor..." : "AKTARIMI ONAYLA"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportToolbar({ selectedIds, onClear, onImport, onImportSupplier, kind }) {
  const alwaysVisible = !!onImportSupplier;
  if (selectedIds.length === 0 && !alwaysVisible) return null;
  const empty = selectedIds.length === 0;
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 bg-blue-950/30 border border-blue-900 rounded-sm px-3 py-2" data-testid={`import-toolbar-${kind}`}>
      <div className="text-xs text-blue-300 font-mono" data-testid={`import-toolbar-count-${kind}`}>
        {empty
          ? "Aktarmak için listeden cari seçin — seçim sonrası bu butonlar aktifleşir"
          : `${selectedIds.length} ${kind === "products" ? "ürün" : "cari"} seçildi`}
      </div>
      <div className="flex gap-2 flex-wrap">
        {!empty && (
          <button onClick={onClear} className="px-2 py-1 text-[11px] rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-900">
            Seçimi Temizle
          </button>
        )}
        {alwaysVisible ? (
          <>
            <button
              data-testid={`import-selected-${kind}`}
              onClick={onImport}
              disabled={empty}
              className="px-3 py-1 text-[11px] rounded-sm bg-blue-500 hover:bg-blue-600 text-slate-950 font-semibold disabled:opacity-40 disabled:hover:bg-blue-500"
            >
              Müşteri Olarak Aktar
            </button>
            <button
              data-testid={`import-selected-${kind}-supplier`}
              onClick={onImportSupplier}
              disabled={empty}
              className="px-3 py-1 text-[11px] rounded-sm bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold disabled:opacity-40 disabled:hover:bg-amber-500"
            >
              Tedarikçi Olarak Aktar
            </button>
          </>
        ) : (
          <button
            data-testid={`import-selected-${kind}`}
            onClick={onImport}
            className="px-3 py-1 text-[11px] rounded-sm bg-blue-500 hover:bg-blue-600 text-slate-950 font-semibold"
          >
            Seçilenleri ERP'ye Aktar
          </button>
        )}
      </div>
    </div>
  );
}

function ProductsList() {
  const { rows, loading, q, setQ, reload } = useMirror("/bizim-hesap/products");
  const [selected, setSelected] = React.useState(new Set());
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // FAZ B2 — Per-bh_id supply_type map (kullanıcı seçimi). Boş kalırsa unknown.
  const [supplyTypeMap, setSupplyTypeMap] = React.useState({});
  // FAZ B M2 — Candidate satırlarında kullanıcı seçimi: "link" | "new" | "skip".
  // Preview'da candidate döndükçe default "new" olur.
  const [candidateDecisions, setCandidateDecisions] = React.useState({});
  const setSupply = (bh_id, val) => setSupplyTypeMap((m) => ({ ...m, [bh_id]: val }));
  const [savingSupply, setSavingSupply] = React.useState(new Set());
  // Aktarılmış ürünlerde tedarik tipi değişikliği ANINDA ERP ürününe kaydedilir.
  const saveSupply = async (r, val) => {
    const prev = supplyTypeMap[r.bh_id] ?? r.erp_supply_type ?? "unknown";
    setSupply(r.bh_id, val);
    setSavingSupply((s) => new Set(s).add(r.bh_id));
    try {
      await api.patch(`/products/${r.linked_erp_id}`, { supply_type: val });
      toast.success(`Tedarik tipi kaydedildi: ${r.title}`);
    } catch (e) {
      setSupply(r.bh_id, prev);
      toast.error(formatApiError(e));
    } finally {
      setSavingSupply((s) => { const n = new Set(s); n.delete(r.bh_id); return n; });
    }
  };
  const bulkSupply = (val) => {
    const next = { ...supplyTypeMap };
    [...selected].forEach((id) => { next[id] = val; });
    setSupplyTypeMap(next);
  };
  const toggle = (bh_id) => setSelected((s) => { const n = new Set(s); n.has(bh_id) ? n.delete(bh_id) : n.add(bh_id); return n; });
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.bh_id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); if (allChecked) rows.forEach((r) => n.delete(r.bh_id)); else rows.forEach((r) => n.add(r.bh_id)); return n; });

  const openPreview = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await api.post("/bizim-hesap/import/products/preview", { bh_ids: ids });
      // Candidate default kararı: "new" (auto-link YOK — kullanıcı isterse değiştirir)
      const initDecisions = {};
      (data.items || []).forEach((r) => {
        if (r.status === "candidate") initDecisions[r.bh_id] = "new";
      });
      setCandidateDecisions(initDecisions);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      // FAZ B M2 — Aktarım anahtarları:
      //   - new / matched satırları: import et
      //   - candidate satırlarında kullanıcı seçimine göre link_map / skip_bh_ids
      const link_map = {};
      const skip_bh_ids = [];
      const ids = [];
      (preview.items || []).forEach((r) => {
        if (r.status === "already_linked" || r.status === "missing" || r.status === "error") return;
        if (r.status === "candidate") {
          const dec = candidateDecisions[r.bh_id] || "new";
          if (dec === "skip") { skip_bh_ids.push(r.bh_id); return; }
          if (dec === "link" && r.candidate_erp_id) {
            link_map[r.bh_id] = r.candidate_erp_id;
          }
          ids.push(r.bh_id);
        } else if (r.status === "new" || r.status === "matched") {
          ids.push(r.bh_id);
        }
      });
      const st_map = {};
      ids.forEach((id) => { if (supplyTypeMap[id]) st_map[id] = supplyTypeMap[id]; });
      const { data } = await api.post("/bizim-hesap/import/products", {
        bh_ids: ids, supply_type_map: st_map,
        link_map, skip_bh_ids,
      });
      const c = data.counts || {};
      toast.success(`Aktarım tamam · Yeni: ${c.created} · Eşleşme: ${c.matched} · Atlanan: ${c.skipped} · Hata: ${c.error}`);
      setPreview(null); setSelected(new Set()); setSupplyTypeMap({}); setCandidateDecisions({}); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const [importAllPreview, setImportAllPreview] = React.useState(null);
  const [importAllBusy, setImportAllBusy] = React.useState(false);
  const importAllDryRun = async () => {
    setImportAllBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/import/products/all?dry_run=true");
      setImportAllPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportAllBusy(false); }
  };
  const importAllExecute = async () => {
    if (!importAllPreview) return;
    if (!window.confirm(`${importAllPreview.candidate_count} ürünün tümü ERP'ye aktarılacak. supply_type=unknown ile başlar. Devam?`)) return;
    setImportAllBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/import/products/all?dry_run=false");
      const c = data.counts || {};
      toast.success(`Tümü aktarıldı · Yeni: ${c.created} · Eşleşme: ${c.matched} · Atlanan: ${c.skipped} · Hata: ${c.error}`);
      setImportAllPreview(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportAllBusy(false); }
  };

  return (
    <div className="space-y-3">
      <SearchBar q={q} setQ={setQ} placeholder="Ürün adı / kod / barkod..." />

      {/* FAZ B — Tümünü Aktar */}
      <div className="border border-blue-500/30 bg-blue-500/5 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="bh-import-all-bar">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-wider font-mono text-blue-400 mb-0.5">Tümünü ERP'ye Aktar</div>
          <div className="text-[11px] text-slate-400">
            supply_type=unknown ile başlar. Duplicate-safe, idempotent. Zaten aktarılmış ürünler ATLANIR.
          </div>
        </div>
        <Button onClick={importAllDryRun} disabled={importAllBusy} data-testid="bh-import-all-preview"
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-sm">
          {importAllBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
          Önizle
        </Button>
        {importAllPreview && (
          <Button onClick={importAllExecute} disabled={importAllBusy} data-testid="bh-import-all-execute"
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm">
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Tümünü Aktar ({importAllPreview.candidate_count})
          </Button>
        )}
      </div>
      {importAllPreview && (
        <div className="border border-slate-800 rounded-sm p-3 bg-slate-950/40 text-xs" data-testid="bh-import-all-preview-list">
          <div className="text-slate-400 mb-1">İlk 20 aday:</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-slate-500 text-[11px]">
            {(importAllPreview.sample || []).map((s) => (
              <div key={s.bh_id} className="truncate">• {s.title}</div>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2">{importAllPreview.note}</div>
        </div>
      )}

      <ImportToolbar selectedIds={[...selected]} onClear={() => setSelected(new Set())} onImport={openPreview} kind="products" />
      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border border-amber-500/30 bg-amber-500/5 rounded-sm text-xs" data-testid="bh-bulk-supply-toolbar">
          <span className="text-amber-400 font-mono uppercase tracking-wider text-[10px]">Seçilen {selected.size} ürün için tedarik tipi:</span>
          <button onClick={() => bulkSupply("manufactured")} className="px-2 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20" data-testid="bh-bulk-manufactured">Üretilen</button>
          <button onClick={() => bulkSupply("traded")} className="px-2 py-1 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20" data-testid="bh-bulk-traded">Hazır / Ticari</button>
          <button onClick={() => bulkSupply("outsourced")} className="px-2 py-1 rounded-sm bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20" data-testid="bh-bulk-outsourced">Fason</button>
          <button onClick={() => bulkSupply("unknown")} className="px-2 py-1 rounded-sm bg-slate-700/40 border border-slate-600 text-slate-400 hover:bg-slate-700/60" data-testid="bh-bulk-unknown">Belirsiz</button>
        </div>
      )}
      <div className="overflow-x-auto border border-slate-800 rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 w-8"><input type="checkbox" data-testid="bh-select-all-products" checked={allChecked} onChange={toggleAll} /></th>
              <th className="text-left px-3 py-2 w-14">Görsel</th>
              <th className="text-left px-3 py-2">Kod</th>
              <th className="text-left px-3 py-2">Barkod</th>
              <th className="text-left px-3 py-2">Başlık</th>
              <th className="text-left px-3 py-2">Tedarik</th>
              <th className="text-left px-3 py-2">Varyant</th>
              <th className="text-right px-3 py-2">Fiyat</th>
              <th className="text-right px-3 py-2">KDV</th>
              <th className="text-right px-3 py-2">Miktar</th>
              <th className="text-left px-3 py-2">Durum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 ? <EmptyRow colSpan={11} loading={loading} /> : rows.map((r) => (
              <tr key={r.bh_id} className={`text-slate-300 hover:bg-slate-900/40 ${selected.has(r.bh_id) ? "bg-blue-950/20" : ""}`} data-testid={`bh-product-row-${r.bh_id}`}>
                <td className="px-3 py-2"><input type="checkbox" data-testid={`bh-select-product-${r.bh_id}`} checked={selected.has(r.bh_id)} onChange={() => toggle(r.bh_id)} /></td>
                <td className="px-3 py-2">
                  {r.photo_url ? (
                    <img src={r.photo_url} alt={r.title} loading="lazy" className="h-10 w-10 rounded-sm border border-slate-700 object-cover bg-slate-900" data-testid={`bh-product-photo-${r.bh_id}`} />
                  ) : (
                    <div className="h-10 w-10 rounded-sm border border-slate-800 bg-slate-900/60 flex items-center justify-center text-slate-700" data-testid={`bh-product-no-photo-${r.bh_id}`}>
                      <ImageIcon className="h-4 w-4" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.code || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.barcode || "—"}</td>
                <td className="px-3 py-2">{r.title}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <select
                      className="bg-slate-950 border border-slate-700 rounded-sm px-1.5 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={supplyTypeMap[r.bh_id] ?? (r.linked_erp_id ? (r.erp_supply_type || "unknown") : "unknown")}
                      onChange={(e) => r.linked_erp_id ? saveSupply(r, e.target.value) : setSupply(r.bh_id, e.target.value)}
                      disabled={savingSupply.has(r.bh_id)}
                      title={r.linked_erp_id ? "Değişiklik anında kaydedilir" : "Aktarımda uygulanır"}
                      data-testid={`bh-supply-${r.bh_id}`}
                    >
                      <option value="unknown">Belirsiz</option>
                      <option value="manufactured">Üretilen</option>
                      <option value="traded">Hazır / Ticari</option>
                      <option value="outsourced">Fason</option>
                    </select>
                    {savingSupply.has(r.bh_id) && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-400">{r.variant || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.price, r.currency || "TL")}</td>
                <td className="px-3 py-2 text-right font-mono">%{r.tax ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono">{r.quantity ?? 0}</td>
                <td className="px-3 py-2 text-[10px]">{r.linked_erp_id ? <span className="text-emerald-400">✓ Aktarılmış</span> : <span className="text-slate-500">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ImportPreviewModal open={!!preview} onClose={() => setPreview(null)} kind="products" items={preview || {}} onConfirm={doImport} busy={busy} decisions={candidateDecisions} setDecisions={setCandidateDecisions} />
    </div>
  );
}

function Pager({ page, totalPages, total, onPage }) {
  if (totalPages <= 1) return <div className="text-[11px] text-slate-500" data-testid="bh-customers-pager-info">Toplam {total} kayıt · 1 sayfa</div>;
  const nums = [...new Set([1, page - 1, page, page + 1, totalPages])].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const btn = "h-7 min-w-7 px-2 rounded-sm border border-slate-800 text-[11px] font-mono disabled:opacity-40 hover:bg-slate-800";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="bh-customers-pager">
      <button className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="bh-customers-prev">Önceki</button>
      {nums.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && n - nums[i - 1] > 1 && <span className="text-slate-500 text-[11px]">…</span>}
          <button className={`${btn} ${n === page ? "bg-blue-600 border-blue-600 text-white" : ""}`} onClick={() => onPage(n)} data-testid={`bh-customers-page-${n}`}>{n}</button>
        </React.Fragment>
      ))}
      <button className={btn} disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="bh-customers-next">Sonraki</button>
      <span className="text-[11px] text-slate-500 ml-2" data-testid="bh-customers-pager-info">Toplam {total} kayıt · {totalPages} sayfa · Sayfa {page}</span>
    </div>
  );
}

function CustomersList() {
  const [q, setQ] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [tick, setTick] = React.useState(0);
  const [res, setRes] = React.useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => { setPage(1); }, [q]);
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/bizim-hesap/customers", { params: { search: q, page, page_size: 500 } });
        if (!cancelled) setRes(data);
      } catch (e) { if (!cancelled) { toast.error(formatApiError(e)); setRes({ items: [], total: 0, total_pages: 1 }); } }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, page, tick]);
  const rows = res.items || [];
  const reload = () => setTick((n) => n + 1);
  const [selected, setSelected] = React.useState(new Set());
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [asSupplier, setAsSupplier] = React.useState(false);
  const toggle = (bh_id) => setSelected((s) => { const n = new Set(s); n.has(bh_id) ? n.delete(bh_id) : n.add(bh_id); return n; });
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.bh_id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); if (allChecked) rows.forEach((r) => n.delete(r.bh_id)); else rows.forEach((r) => n.add(r.bh_id)); return n; });

  const openPreview = async (targetIsSupplier = false) => {
    const ids = [...selected];
    if (!ids.length) return;
    setAsSupplier(targetIsSupplier);
    try {
      const { data } = await api.post("/bizim-hesap/import/customers/preview", { bh_ids: ids, as_supplier: targetIsSupplier });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const ids = (preview.items || []).filter((r) => r.status === "new" || r.status === "matched").map((r) => r.bh_id);
      const { data } = await api.post("/bizim-hesap/import/customers", { bh_ids: ids, as_supplier: asSupplier });
      const c = data.counts || {};
      toast.success(`${asSupplier ? "Tedarikçi" : "Müşteri"} aktarım tamam · Yeni: ${c.created} · Eşleşme: ${c.matched} · Atlanan: ${c.skipped} · Hata: ${c.error}`);
      setPreview(null); setSelected(new Set()); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <SearchBar q={q} setQ={setQ} placeholder="Cari adı / kod / vergi no / telefon..." />
      <ImportToolbar selectedIds={[...selected]} onClear={() => setSelected(new Set())} onImport={() => openPreview(false)} onImportSupplier={() => openPreview(true)} kind="customers" />
      <div className="text-[11px] text-slate-500" data-testid="bh-customers-select-hint">
        Başlıktaki kutu yalnız <b className="text-slate-300">bu sayfadaki {rows.length}</b> cariyi seçer/kaldırır · Toplam seçili (tüm sayfalar): <b className="text-slate-300" data-testid="bh-customers-selected-total">{selected.size}</b>
      </div>
      <div className="overflow-x-auto border border-slate-800 rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 w-8"><input type="checkbox" data-testid="bh-select-all-customers" title="Bu sayfadakileri seç" checked={allChecked} onChange={toggleAll} /></th>
              <th className="text-left px-3 py-2">Kod</th>
              <th className="text-left px-3 py-2">Ünvan</th>
              <th className="text-left px-3 py-2">Vergi No / Daire</th>
              <th className="text-left px-3 py-2">Telefon</th>
              <th className="text-left px-3 py-2">E-posta</th>
              <th className="text-right px-3 py-2">Bakiye</th>
              <th className="text-left px-3 py-2">Durum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 ? <EmptyRow colSpan={8} loading={loading} /> : rows.map((r) => (
              <tr key={r.bh_id} className={`text-slate-300 hover:bg-slate-900/40 ${selected.has(r.bh_id) ? "bg-blue-950/20" : ""}`} data-testid={`bh-customer-row-${r.bh_id}`}>
                <td className="px-3 py-2"><input type="checkbox" data-testid={`bh-select-customer-${r.bh_id}`} checked={selected.has(r.bh_id)} onChange={() => toggle(r.bh_id)} /></td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.code || "—"}</td>
                <td className="px-3 py-2">{r.title}</td>
                <td className="px-3 py-2 text-slate-400">
                  {r.tax_no || "—"}
                  {r.tax_office ? <span className="text-slate-500"> · {r.tax_office}</span> : null}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.phone || "—"}</td>
                <td className="px-3 py-2 text-slate-400">{r.email || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{r.balance || "—"}</td>
                <td className="px-3 py-2 text-[10px]">{r.linked_erp_id ? <span className="text-emerald-400">✓ Aktarılmış</span> : <span className="text-slate-500">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={res.total_pages || 1} total={res.total || 0} onPage={setPage} />
      <ImportPreviewModal open={!!preview} onClose={() => setPreview(null)} kind="customers" items={preview || {}} onConfirm={doImport} busy={busy} />
    </div>
  );
}

function WarehousesList() {
  const { rows, loading } = useMirror("/bizim-hesap/warehouses");
  const [erpWarehouses, setErpWarehouses] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [editing, setEditing] = useState({});  // {bh_id: erp_wh_id_selection}
  const [busy, setBusy] = useState({});         // {bh_id: bool}
  const [syncBusy, setSyncBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);

  const loadMappings = React.useCallback(async () => {
    try {
      const [erp, mp] = await Promise.all([
        api.get("/warehouses"),
        api.get("/bizim-hesap/warehouse-mapping"),
      ]);
      setErpWarehouses(erp.data || []);
      setMappings(mp.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { loadMappings(); }, [loadMappings]);

  const mapByBh = Object.fromEntries(mappings.map((m) => [m.bh_wh_id, m]));

  const saveMapping = async (bh_id) => {
    const erp_wh_id = editing[bh_id];
    if (!erp_wh_id) return toast.error("ERP deposu seçin");
    setBusy((b) => ({ ...b, [bh_id]: true }));
    try {
      await api.post("/bizim-hesap/warehouse-mapping",
        { bh_wh_id: bh_id, erp_wh_id });
      toast.success("Eşleştirildi");
      setEditing((e) => { const n = { ...e }; delete n[bh_id]; return n; });
      loadMappings();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [bh_id]: false })); }
  };
  const removeMapping = async (bh_id) => {
    if (!window.confirm("Bu eşleşmeyi kaldırmak istediğinize emin misiniz?")) return;
    setBusy((b) => ({ ...b, [bh_id]: true }));
    try {
      await api.delete(`/bizim-hesap/warehouse-mapping/${bh_id}`);
      toast.success("Eşleşme kaldırıldı");
      loadMappings();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [bh_id]: false })); }
  };

  const runDryRun = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/sync/stock-snapshot",
        { dry_run: true });
      setDryRun(data);
      // FAZ B M2+ — will_write, will_create, will_update backend'ten gelir
      // (200→246 sayaç düzeltmesi: `changes.length` slice sınırlı 200'de kırpılır,
      // `will_write` gerçek yazılacak toplamdır).
      const total = data.will_write ?? (data.changes || []).length;
      if (total === 0) {
        toast.info("Hiç değişiklik bulunamadı (tüm stoklar güncel veya eşleşme yok)");
      } else {
        const parts = [];
        if (data.will_create) parts.push(`${data.will_create} yeni kayıt`);
        if (data.will_update) parts.push(`${data.will_update} mevcut güncelleme`);
        const detail = parts.length ? ` (${parts.join(" · ")})` : "";
        toast.success(`Toplam ${total} yazma hazır${detail}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncBusy(false); }
  };
  const runSync = async () => {
    if (!dryRun) return;
    const total = dryRun.will_write ?? (dryRun.changes || []).length;
    if (total === 0) return;
    const parts = [];
    if (dryRun.will_create) parts.push(`${dryRun.will_create} yeni oluşturulacak`);
    if (dryRun.will_update) parts.push(`${dryRun.will_update} mevcut kayıt değişecek`);
    const detail = parts.length ? `\n\n• ${parts.join("\n• ")}` : "";
    if (!window.confirm(`Toplam ${total} stok yazması yapılacak.${detail}\n\nOnaylıyor musunuz?`)) return;
    setSyncBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/sync/stock-snapshot",
        { dry_run: false });
      toast.success(`Sync tamam · Yazılan: ${data.written} (yeni: ${data.will_create}, güncel: ${data.will_update}) · Atlanan (native): ${data.skipped_erp_native} · Atlanan (ürün yok): ${data.skipped_no_product}`);
      setDryRun(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncBusy(false); }
  };

  const mappedCount = mappings.length;
  const totalCount = rows.length;

  // Enrich dry-run changes with BH warehouse title (via mapping)
  const bhByErpWh = Object.fromEntries(
    mappings.map((m) => [m.erp_wh_id, m.bh_title || m.bh_wh_id.slice(0, 8)])
  );

  return (
    <div className="space-y-4">
      {/* Summary + Sync Actions */}
      <div className="border border-blue-500/30 bg-blue-500/5 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="bh-warehouse-sync-bar">
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-wider font-mono text-blue-400 mb-0.5">Stok Senkronizasyonu</div>
          <div className="text-[11px] text-slate-400">
            {mappedCount} / {totalCount} depo eşleştirildi ·
            SADECE eşleşmiş depolar senkronize edilir · Manuel/ERP-native stoklar EZİLMEZ · Idempotent
          </div>
        </div>
        <Button onClick={runDryRun} disabled={syncBusy || mappedCount === 0} data-testid="wh-sync-dryrun"
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-sm">
          {syncBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
          Önizle (Dry-run)
        </Button>
        {dryRun && ((dryRun.will_write ?? (dryRun.changes || []).length) > 0) && (
          <Button onClick={runSync} disabled={syncBusy} data-testid="wh-sync-execute"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm">
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Tümünü Senkronize Et ({dryRun.will_write ?? dryRun.changes.length})
          </Button>
        )}
      </div>

      {/* Inline mapping table */}
      <div className="overflow-x-auto border border-slate-800 rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Bizim Hesap Deposu</th>
              <th className="text-left px-3 py-2">Depo ID</th>
              <th className="text-left px-3 py-2 w-[280px]">ERP Deposu</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-right px-3 py-2 w-[160px]">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 ? <EmptyRow colSpan={5} loading={loading} /> : rows.map((r) => {
              const mp = mapByBh[r.bh_id];
              const editingVal = editing[r.bh_id];
              const isEditing = editingVal !== undefined;
              const isBusy = busy[r.bh_id];
              return (
                <tr key={r.bh_id} className={`text-slate-300 ${mp ? "bg-emerald-950/10" : ""}`} data-testid={`bh-warehouse-row-${r.bh_id}`}>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.bh_id.slice(0, 12)}...</td>
                  <td className="px-3 py-2">
                    {mp && !isEditing ? (
                      <div className="text-slate-300" data-testid={`wh-map-current-${r.bh_id}`}>
                        <span className="text-blue-400 mr-1">→</span>
                        {mp.erp_name} <span className="text-[10px] text-slate-500 ml-1 font-mono">({mp.erp_code})</span>
                      </div>
                    ) : (
                      <Select value={editingVal || ""} onValueChange={(v) => setEditing((e) => ({ ...e, [r.bh_id]: v }))}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs" data-testid={`wh-map-select-${r.bh_id}`}>
                          <SelectValue placeholder="ERP deposu seç..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-72">
                          {erpWarehouses.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.name} <span className="text-[10px] text-slate-500 ml-1 font-mono">({w.code})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {mp ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono border bg-emerald-500/10 border-emerald-500/30 text-emerald-400" data-testid={`wh-status-mapped-${r.bh_id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Eşleştirildi
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono border bg-rose-500/10 border-rose-500/30 text-rose-400" data-testid={`wh-status-unmapped-${r.bh_id}`}>
                        <XCircle className="w-3 h-3" /> Eşleştirilmedi
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {mp && !isEditing ? (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditing((e) => ({ ...e, [r.bh_id]: mp.erp_wh_id }))}
                          data-testid={`wh-map-change-${r.bh_id}`}
                          disabled={isBusy}
                          className="text-[10px] px-2 py-1 rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-300"
                        >Değiştir</button>
                        <button
                          onClick={() => removeMapping(r.bh_id)}
                          data-testid={`wh-map-remove-${r.bh_id}`}
                          disabled={isBusy}
                          className="text-[10px] px-2 py-1 rounded-sm bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30"
                        >Kaldır</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => saveMapping(r.bh_id)}
                          disabled={isBusy || !editingVal}
                          data-testid={`wh-map-save-${r.bh_id}`}
                          className="text-[10px] px-2 py-1 rounded-sm bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                        >{mp ? "Kaydet" : "Eşleştir"}</button>
                        {isEditing && (
                          <button
                            onClick={() => setEditing((e) => { const n = { ...e }; delete n[r.bh_id]; return n; })}
                            className="text-[10px] px-2 py-1 rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-400"
                          >İptal</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dry-run preview */}
      {dryRun && (
        <div className="border border-slate-800 rounded-sm overflow-hidden" data-testid="wh-sync-preview">
          <div className="bg-slate-950/60 px-3 py-2 text-[10px] uppercase font-mono text-slate-400 flex gap-4 flex-wrap">
            <span>İşlenen: <b className="text-slate-200">{dryRun.processed}</b></span>
            <span>Yazılacak: <b className="text-emerald-400">{(dryRun.changes || []).length}</b></span>
            <span>Atlanan (ürün yok): <b className="text-slate-500">{dryRun.skipped_no_product}</b></span>
            <span>Atlanan (ERP native): <b className="text-amber-400">{dryRun.skipped_erp_native}</b></span>
            {dryRun.reason && <span className="text-rose-400">⚠ {dryRun.reason}</span>}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-950/40">
              <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2">BH Deposu</th>
                <th className="text-left px-3 py-2">ERP Deposu</th>
                <th className="text-left px-3 py-2">Ürün</th>
                <th className="text-right px-3 py-2">ERP Önce</th>
                <th className="text-right px-3 py-2">BH Stok</th>
                <th className="text-right px-3 py-2">ERP Sonra</th>
                <th className="text-right px-3 py-2">Δ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {(dryRun.changes || []).slice(0, 100).map((c, i) => {
                const delta = c.erp_qty_after - c.erp_qty_before;
                return (
                  <tr key={i} data-testid={`wh-sync-change-${i}`}>
                    <td className="px-3 py-1.5 text-slate-400 text-[10px]">
                      {bhByErpWh[c.warehouse_id] || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-slate-400 text-[10px] font-mono">{c.warehouse_id.slice(0, 8)}...</td>
                    <td className="px-3 py-1.5 text-slate-300">{c.product_name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-slate-500">{c.erp_qty_before}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-emerald-400">{c.bh_qty}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-blue-400">{c.erp_qty_after}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${delta === 0 ? "text-slate-500" : delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {delta === 0 ? "—" : (delta > 0 ? `+${delta}` : delta)}
                    </td>
                  </tr>
                );
              })}
              {(dryRun.changes || []).length > 100 && (
                <tr><td colSpan={7} className="px-3 py-2 text-center text-[10px] text-slate-500">
                  +{(dryRun.will_write ?? dryRun.changes.length) - 100} daha... (örnek liste ilk 100 satırla sınırlı; toplam {dryRun.will_write ?? dryRun.changes.length})
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InventoryList() {
  const [wid, setWid] = useState("");
  const { rows: wrows } = useMirror("/bizim-hesap/warehouses");
  const { rows, loading, q, setQ } = useMirror("/bizim-hesap/inventory", { warehouse_id: wid });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]"><SearchBar q={q} setQ={setQ} placeholder="Ürün ara..." /></div>
        <div className="min-w-[180px]">
          <Select value={wid || "all"} onValueChange={(v) => setWid(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs" data-testid="bh-warehouse-select">
              <SelectValue placeholder="Tüm depolar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm depolar</SelectItem>
              {wrows.map((w) => (
                <SelectItem key={w.bh_id} value={w.bh_id}>{w.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-800 rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Depo</th>
              <th className="text-left px-3 py-2">Ürün</th>
              <th className="text-right px-3 py-2">Miktar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 ? <EmptyRow colSpan={3} loading={loading} /> : rows.map((r) => (
              <tr key={`${r.warehouse_id}-${r.product_id}`} className="text-slate-300 hover:bg-slate-900/40" data-testid={`bh-inventory-row-${r.product_id}`}>
                <td className="px-3 py-2 text-slate-400">{r.warehouse_title || "—"}</td>
                <td className="px-3 py-2">{r.title}</td>
                <td className="px-3 py-2 text-right font-mono">{r.qty ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// FAZ B3 UI — Depo Eşleştirme + Stok Sync (dry-run önizleme)
// ============================================================================
function WarehouseMappingPanel() {
  const [bhWarehouses, setBhWarehouses] = useState([]);
  const [erpWarehouses, setErpWarehouses] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedBh, setSelectedBh] = useState("");
  const [selectedErp, setSelectedErp] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [bh, erp, mp] = await Promise.all([
        api.get("/bizim-hesap/warehouses"),
        api.get("/warehouses"),
        api.get("/bizim-hesap/warehouse-mapping"),
      ]);
      setBhWarehouses(bh.data || []);
      setErpWarehouses(erp.data || []);
      setMappings(mp.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addMapping = async () => {
    if (!selectedBh || !selectedErp) {
      toast.error("Bizim Hesap ve ERP deposu seçin");
      return;
    }
    try {
      await api.post("/bizim-hesap/warehouse-mapping", {
        bh_wh_id: selectedBh, erp_wh_id: selectedErp,
      });
      toast.success("Depo eşleşmesi kaydedildi");
      setSelectedBh(""); setSelectedErp("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeMapping = async (bh_wh_id) => {
    if (!window.confirm("Bu eşleşmeyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/bizim-hesap/warehouse-mapping/${bh_wh_id}`);
      toast.success("Eşleşme silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runDryRun = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/sync/stock-snapshot",
        { dry_run: true });
      setDryRunResult(data);
      const total = data.will_write ?? (data.changes?.length || 0);
      toast.success(`Önizleme hazır: toplam ${total} yazma (yeni: ${data.will_create || 0}, güncel: ${data.will_update || 0})`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncBusy(false); }
  };
  const runSync = async () => {
    if (!dryRunResult) return;
    const total = dryRunResult.will_write ?? (dryRunResult.changes?.length || 0);
    if (total === 0) return;
    if (!window.confirm(`Toplam ${total} stok yazması yapılacak (yeni: ${dryRunResult.will_create || 0}, güncel: ${dryRunResult.will_update || 0}). Devam?`)) return;
    setSyncBusy(true);
    try {
      const { data } = await api.post("/bizim-hesap/sync/stock-snapshot",
        { dry_run: false });
      toast.success(`Senkron tamam · Yazılan: ${data.written} · Atlanan (native): ${data.skipped_erp_native} · Atlanan (ürün yok): ${data.skipped_no_product}`);
      setDryRunResult(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncBusy(false); }
  };

  const usedBhIds = new Set(mappings.map((m) => m.bh_wh_id));
  const availableBh = bhWarehouses.filter((w) => !usedBhIds.has(w.bh_id));

  return (
    <div className="space-y-4" data-testid="bh-mapping-panel">
      {/* Add mapping */}
      <div className="border border-slate-800 bg-slate-950/40 rounded-sm p-4">
        <div className="text-xs uppercase tracking-wider font-mono text-slate-400 mb-3 flex items-center gap-2">
          <Link2 className="w-3.5 h-3.5 text-blue-400" /> Yeni Depo Eşleştirmesi
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-slate-500 font-mono">Bizim Hesap Deposu</Label>
            <Select value={selectedBh} onValueChange={setSelectedBh}>
              <SelectTrigger data-testid="mapping-bh-select" className="bg-slate-950 border-slate-700 rounded-sm mt-1"><SelectValue placeholder="Seç..." /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                {availableBh.length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-slate-500">Eşleşmemiş BH deposu yok</div>
                )}
                {availableBh.map((w) => (
                  <SelectItem key={w.bh_id} value={w.bh_id}>{w.title || w.bh_id.slice(0, 8)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500 font-mono">ERP Deposu</Label>
            <Select value={selectedErp} onValueChange={setSelectedErp}>
              <SelectTrigger data-testid="mapping-erp-select" className="bg-slate-950 border-slate-700 rounded-sm mt-1"><SelectValue placeholder="Seç..." /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                {erpWarehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name} <span className="text-slate-500 ml-1">({w.code})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={addMapping} data-testid="mapping-add-btn" className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm w-full">
              <Link2 className="w-3.5 h-3.5 mr-1" /> Eşleştir
            </Button>
          </div>
        </div>
      </div>

      {/* Existing mappings */}
      <div className="border border-slate-800 rounded-sm">
        <table className="w-full text-xs" data-testid="mapping-table">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Bizim Hesap Deposu</th>
              <th className="text-left px-3 py-2">↔</th>
              <th className="text-left px-3 py-2">ERP Deposu</th>
              <th className="text-left px-3 py-2">Kod</th>
              <th className="text-right px-3 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
              : mappings.length === 0 ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Henüz eşleşme yok. Yukarıdan oluşturun.</td></tr>
                : mappings.map((m) => (
                  <tr key={m.bh_wh_id} data-testid={`mapping-row-${m.bh_wh_id}`}>
                    <td className="px-3 py-2 text-slate-300">{m.bh_title || m.bh_wh_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-blue-400">→</td>
                    <td className="px-3 py-2 text-slate-300">{m.erp_name || "?"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{m.erp_code}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeMapping(m.bh_wh_id)} data-testid={`mapping-delete-${m.bh_wh_id}`} className="text-rose-400 hover:text-rose-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Sync section */}
      <div className="border border-blue-500/30 bg-blue-500/5 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <PlayCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-200 mb-1">Stok Senkronizasyonu</div>
            <div className="text-[11px] text-slate-500 mb-3">
              Sadece eşleşmiş depolar + Bizim Hesap kaynağı ERP ürünler için stok bakiyesi yansıtılır.
              Manuel/ERP-native stoklar EZİLMEZ. Aynı sync tekrar çalıştırılırsa stok değişmez (idempotent).
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={runDryRun} disabled={syncBusy || mappings.length === 0} data-testid="sync-dryrun-btn"
                      className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-sm">
                {syncBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
                Önizle (Dry-run)
              </Button>
              {dryRunResult && (
                <Button onClick={runSync} disabled={syncBusy} data-testid="sync-execute-btn"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm">
                  <PlayCircle className="w-3.5 h-3.5 mr-1" /> Senkronize Et ({dryRunResult.will_write ?? (dryRunResult.changes?.length || 0)} yazma)
                </Button>
              )}
            </div>
            {dryRunResult && (
              <div className="mt-4 border border-slate-800 rounded-sm overflow-hidden" data-testid="sync-preview-table">
                <div className="bg-slate-950/60 px-3 py-2 text-[10px] uppercase font-mono text-slate-400 flex gap-4 flex-wrap">
                  <span>İşlenen: <b className="text-slate-200">{dryRunResult.processed}</b></span>
                  <span>Yeni: <b className="text-emerald-400">{dryRunResult.will_create || 0}</b></span>
                  <span>Güncel: <b className="text-blue-400">{dryRunResult.will_update || 0}</b></span>
                  <span>Toplam yazma: <b className="text-emerald-400">{dryRunResult.will_write ?? (dryRunResult.changes?.length || 0)}</b></span>
                  <span>Atlanan (ürün yok): <b className="text-slate-500">{dryRunResult.skipped_no_product}</b></span>
                  <span>Atlanan (ERP native): <b className="text-amber-400">{dryRunResult.skipped_erp_native}</b></span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-950/40">
                    <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                      <th className="text-left px-3 py-2">Ürün</th>
                      <th className="text-right px-3 py-2">ERP Stok (Önce)</th>
                      <th className="text-right px-3 py-2">BH Stok</th>
                      <th className="text-right px-3 py-2">ERP Stok (Sonra)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {(dryRunResult.changes || []).slice(0, 50).map((c, i) => (
                      <tr key={i} data-testid={`sync-change-${i}`}>
                        <td className="px-3 py-1.5 text-slate-300">{c.product_name}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-500">{c.erp_qty_before}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-400">{c.bh_qty}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-blue-400">{c.erp_qty_after}</td>
                      </tr>
                    ))}
                    {(dryRunResult.changes || []).length > 50 && (
                      <tr><td colSpan={4} className="px-3 py-2 text-center text-[10px] text-slate-500">+{(dryRunResult.will_write ?? dryRunResult.changes.length) - 50} daha... (örnek liste ilk 50 ile sınırlı; toplam {dryRunResult.will_write ?? dryRunResult.changes.length})</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// FAZ B4 UI — Üretim Riski Raporu (salt-okunur)
// ============================================================================
function ProductionRiskPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bizim-hesap/production-risk-report");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-xs text-slate-500 py-8 text-center">Yükleniyor...</div>;
  if (!data) return null;

  const Section = ({ title, items, tone, testId }) => (
    <div className={`border ${tone === "danger" ? "border-rose-500/30 bg-rose-500/5" : "border-amber-500/30 bg-amber-500/5"} rounded-sm`} data-testid={testId}>
      <div className={`px-3 py-2 text-xs uppercase tracking-wider font-mono ${tone === "danger" ? "text-rose-400" : "text-amber-400"} border-b ${tone === "danger" ? "border-rose-500/20" : "border-amber-500/20"} flex justify-between`}>
        <span>{title}</span>
        <span data-testid={`${testId}-count`}>{items.length} ürün</span>
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-6 text-center text-[11px] text-slate-500">Bu grupta risk yok ✓</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-950/40">
            <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Ürün</th>
              <th className="text-left px-3 py-2">Kategori</th>
              <th className="text-left px-3 py-2">Mevcut Tip</th>
              <th className="text-right px-3 py-2">Sipariş Satırı</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {items.slice(0, 100).map((it) => (
              <tr key={it.product_id} data-testid={`risk-row-${it.product_id}`}>
                <td className="px-3 py-1.5 text-slate-300">{it.product_name}</td>
                <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">{it.category || "—"}</td>
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-mono border ${it.supply_type === "unknown" ? "bg-rose-500/10 border-rose-500/30 text-rose-400" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"}`}>
                    {it.supply_type === "unknown" ? "BELİRSİZ" : "ÜRETİLEN"}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right font-mono">{it.order_lines_count}</td>
              </tr>
            ))}
            {items.length > 100 && <tr><td colSpan={4} className="px-3 py-2 text-center text-[10px] text-slate-500">+{items.length - 100} daha...</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="risk-panel">
      <div className="text-[10px] text-slate-500 flex items-center gap-1 bg-slate-950/50 border border-slate-800 rounded-sm px-3 py-2">
        <Info className="w-3 h-3" /> Bu rapor SALT-OKUNURDUR. Otomatik silme/düzenleme YAPILMAZ.
        Karar vermek için ilgili ürünün detayından tedarik tipini güncelleyin.
      </div>
      <Section
        title="⚠ Karar Bekleyen — Tedarik Tipi Belirlenmemiş"
        items={data.items_awaiting_decision || []}
        tone="warn"
        testId="risk-awaiting"
      />
      <Section
        title="🔴 Riskli — BH ürün ama Üretilen olarak işaretli"
        items={data.items_wrong_manufactured || []}
        tone="danger"
        testId="risk-manufactured"
      />
      <div className="text-right">
        <Button onClick={load} data-testid="risk-refresh-btn" className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-sm text-xs">
          <RefreshCcw className="w-3 h-3 mr-1" /> Yenile
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// FAZ B M4 — Varyant Eşleştirme Panel (provider-agnostic UI, first: bizim_hesap)
// ============================================================================

function VariantMappingPanel() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [erpProducts, setErpProducts] = React.useState([]);
  const [erpWarehouses, setErpWarehouses] = React.useState([]);
  const [variantsByProduct, setVariantsByProduct] = React.useState({});
  const [drafts, setDrafts] = React.useState({}); // key -> {erp_product_id, erp_variant_id, erp_warehouse_id}
  const [dryRun, setDryRun] = React.useState(null);

  const rowKey = (r) =>
    `${r.external_product_id}::${r.external_variant_label || "__"}::${r.external_warehouse_id}`;

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/bizim_hesap/mapping-candidates?unmapped_only=true&limit=500");
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adaylar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const loadErp = async () => {
    try {
      const [p, w] = await Promise.all([
        api.get("/products?limit=1000"),
        api.get("/warehouses"),
      ]);
      setErpProducts(p.data || []);
      setErpWarehouses(w.data || []);
    } catch {
      /* sessiz */
    }
  };

  React.useEffect(() => {
    loadCandidates();
    loadErp();
  }, []);

  const loadVariantsFor = async (productId) => {
    if (!productId || variantsByProduct[productId]) return;
    try {
      const { data } = await api.get(`/products/${productId}/variants`);
      setVariantsByProduct((prev) => ({ ...prev, [productId]: data || [] }));
    } catch {
      setVariantsByProduct((prev) => ({ ...prev, [productId]: [] }));
    }
  };

  const setDraft = (key, patch) =>
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const applySuggestion = (row) => {
    if (!row.suggested_erp_product_id) return;
    setDraft(rowKey(row), { erp_product_id: row.suggested_erp_product_id });
    loadVariantsFor(row.suggested_erp_product_id);
  };

  const saveMapping = async (row) => {
    const k = rowKey(row);
    const d = drafts[k] || {};
    if (!d.erp_product_id || !d.erp_warehouse_id) {
      toast.error("ERP ürün ve depo seçimi zorunlu");
      return;
    }
    try {
      await api.post("/integrations/bizim_hesap/mappings", {
        provider: "bizim_hesap",
        external_product_id: row.external_product_id,
        external_variant_label: row.external_variant_label,
        external_warehouse_id: row.external_warehouse_id,
        erp_product_id: d.erp_product_id,
        erp_variant_id: d.erp_variant_id || null,
        erp_warehouse_id: d.erp_warehouse_id,
        status: "active",
      });
      toast.success("Eşleştirme kaydedildi");
      await loadCandidates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt hatası");
    }
  };

  const runDryRun = async () => {
    try {
      const { data } = await api.post("/integrations/bizim_hesap/stock-sync/dry-run");
      setDryRun(data);
      toast.success(`Dry-run: ${data.counts?.mapped || 0} eşleşmiş, ${data.counts?.unmapped || 0} eksik`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run hatası");
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    return matchesAll(q, [r.raw_title, r.external_variant_label, r.external_product_id]);
  });

  return (
    <div className="space-y-3" data-testid="variant-mapping-panel">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBar q={q} setQ={setQ} placeholder="Ürün, varyant, ID ara..." />
        <Button size="sm" onClick={loadCandidates} disabled={loading}
                data-testid="vm-refresh-btn"
                className="rounded-sm bg-slate-800 hover:bg-slate-700">
          Yenile
        </Button>
        <Button size="sm" onClick={runDryRun}
                data-testid="vm-dry-run-btn"
                className="rounded-sm bg-amber-700 hover:bg-amber-600">
          Dry-Run Çalıştır
        </Button>
        <div className="text-xs text-slate-400">
          Eşleşmemiş: {filtered.length} / {rows.length}
        </div>
      </div>

      {dryRun && (
        <Card className="p-3 bg-amber-950/30 border-amber-800 rounded-sm text-xs" data-testid="vm-dry-run-result">
          <div className="font-semibold text-amber-200 mb-1">Dry-Run Özeti (STOK YAZIMI YAPILMADI)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-slate-300">
            <div>Toplam: <b>{dryRun.counts.total}</b></div>
            <div>Eşleşen: <b className="text-emerald-400">{dryRun.counts.mapped}</b></div>
            <div>Eksik: <b className="text-rose-400">{dryRun.counts.unmapped}</b></div>
            <div>Yok Sayıldı: <b>{dryRun.counts.ignored}</b></div>
            <div>Varyant Düzeyi: <b>{dryRun.counts.variant_level}</b></div>
            <div>Ürün Düzeyi: <b>{dryRun.counts.product_level}</b></div>
            <div>Depo Eşleşti: <b>{dryRun.counts.warehouse_mapped}</b></div>
            <div>Depo Eksik: <b>{dryRun.counts.warehouse_missing}</b></div>
          </div>
          <div className="text-slate-500 mt-1">Hedef: <code>{dryRun.target_collection}</code></div>
        </Card>
      )}

      <div className="border border-slate-800 rounded-sm max-h-[600px] overflow-auto">
        <table className="w-full text-xs" data-testid="vm-table">
          <thead className="bg-slate-950 sticky top-0">
            <tr className="text-slate-400">
              <th className="text-left px-2 py-2">BH Ürün / Varyant</th>
              <th className="text-left px-2 py-2">Stok</th>
              <th className="text-left px-2 py-2">ERP Ürün</th>
              <th className="text-left px-2 py-2">ERP Varyant</th>
              <th className="text-left px-2 py-2">ERP Depo</th>
              <th className="text-left px-2 py-2">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center text-slate-500 py-4">Yükleniyor...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-500 py-4">Eşleşmemiş kayıt yok.</td></tr>
            )}
            {filtered.map((r) => {
              const k = rowKey(r);
              const d = drafts[k] || {};
              const variants = variantsByProduct[d.erp_product_id] || [];
              return (
                <tr key={k} className="border-t border-slate-800 hover:bg-slate-900/40">
                  <td className="px-2 py-1.5">
                    <div className="text-slate-200 truncate max-w-[280px]" title={r.raw_title}>{r.raw_title}</div>
                    <div className="text-slate-500 text-[10px]">
                      {r.external_variant_label ? `Varyant: ${r.external_variant_label}` : <span className="italic">tek-varyant</span>}
                      {!r.variant_parsed_ok && <span className="text-rose-400 ml-1">· parse başarısız</span>}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-slate-300">{r.qty}</td>
                  <td className="px-2 py-1.5">
                    <select
                      data-testid={`vm-erp-product-${r.external_product_id}-${r.external_variant_label || "_"}`}
                      className="bg-slate-900 border border-slate-700 rounded-sm px-1 py-0.5 text-slate-200 max-w-[200px]"
                      value={d.erp_product_id || ""}
                      onChange={(e) => {
                        const pid = e.target.value || null;
                        setDraft(k, { erp_product_id: pid, erp_variant_id: null });
                        if (pid) loadVariantsFor(pid);
                      }}
                    >
                      <option value="">— Seç —</option>
                      {erpProducts.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {r.suggested_erp_product_id && !d.erp_product_id && (
                      <button
                        type="button"
                        className="ml-1 text-emerald-400 text-[10px] underline"
                        data-testid={`vm-apply-suggestion-${r.external_product_id}`}
                        onClick={() => applySuggestion(r)}
                      >
                        öneri: {r.suggested_erp_product_name}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className="bg-slate-900 border border-slate-700 rounded-sm px-1 py-0.5 text-slate-200 max-w-[140px]"
                      value={d.erp_variant_id || ""}
                      onChange={(e) => setDraft(k, { erp_variant_id: e.target.value || null })}
                      disabled={!d.erp_product_id}
                    >
                      <option value="">— (yok) —</option>
                      {variants.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.sku || v.id.slice(0, 6)} {v.attributes?.size ? `· ${v.attributes.size}` : ""}
                          {v.attributes?.color ? ` · ${v.attributes.color}` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className="bg-slate-900 border border-slate-700 rounded-sm px-1 py-0.5 text-slate-200 max-w-[140px]"
                      value={d.erp_warehouse_id || ""}
                      onChange={(e) => setDraft(k, { erp_warehouse_id: e.target.value || null })}
                    >
                      <option value="">— Seç —</option>
                      {erpWarehouses.map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Button
                      size="sm"
                      onClick={() => saveMapping(r)}
                      disabled={!d.erp_product_id || !d.erp_warehouse_id}
                      data-testid={`vm-save-${r.external_product_id}-${r.external_variant_label || "_"}`}
                      className="rounded-sm bg-emerald-700 hover:bg-emerald-600 text-[10px] h-6 px-2"
                    >
                      Eşleştir
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-500">
        Not: Bu ekran <b>hiçbir zaman</b> otomatik ProductVariant oluşturmaz. Barcode
        yardımcı bir sinyaldir; birincil kimlik <code>(BH Ürün, Varyant Etiketi, Depo)</code>.
        Stok yazımı henüz devrede değil — sadece dry-run.
      </div>
    </div>
  );
}

